/**
 * Cohesive onboarding widget — preset registration.
 *
 * The widget node is now an invisible 1×1 "state shell" — it owns the
 * Loro-persisted onboarding state (current step, snapped flag, captured
 * id sets) but renders nothing visible on the canvas plane. The visible
 * coach card is a screen-fixed overlay (`OnboardingOverlay`) mounted
 * once at canvas root, which reads the active step from
 * `CanvasOnboardingContext` and docks itself per-step (centered splash
 * for welcome / gesture / finish; compact corner for drop-heavy steps).
 *
 * See:
 *   • ./OnboardingWidget.tsx       — invisible state shell
 *   • ./OnboardingOverlay.tsx      — screen-docked card (portal)
 *   • ./anchors.ts                 — per-step anchor resolution
 *   • ./steps/index.ts             — ordered step registry (with anchors)
 *   • ./shared/types.ts            — StepProps / StepDefinition contracts
 *   • ./styles.ts                  — shared CSS tokens + keyframes
 */
import type { WidgetPreset } from "../types";
import { OnboardingWidget } from "./OnboardingWidget";
import previewHtml from "./preview.html?raw";

/**
 * The node itself is invisible. We give it a tiny non-zero footprint
 * so React Flow's measurement / layout code doesn't treat it as a
 * degenerate zero-size node, but the rendered shell sets
 * `visibility: hidden` and `pointer-events: none` so it can't be
 * interacted with on the canvas. All visible UX flows through
 * `OnboardingOverlay`.
 */
const SHELL_SIZE = 1;

export const ONBOARDING_WELCOME: WidgetPreset = {
  id: "onboarding-welcome",
  label: "Welcome ✦",
  // Seeded programmatically by `useSeedOnboardingWidget` only — never
  // user-placeable. Keeps it out of the toolbar's widget picker.
  internal: true,
  defaultWidth: SHELL_SIZE,
  defaultHeight: SHELL_SIZE,
  component: OnboardingWidget,
  // No resizable config — the shell stays a tiny invisible square and
  // the overlay sizes itself per-anchor in screen space. Omitting
  // `resizable` also suppresses the NodeResizer chrome that would
  // otherwise render around a (theoretical) selectable node.
  previewHtml,
  initialData: { started: false, snapped: false, step: 1 },
  // The state shell is non-interactive on the canvas — no selection
  // ring, no trash toolbar, no drag. The user drives onboarding
  // through the screen-docked overlay's own controls and the
  // highlighted canvas chrome.
  nodeProps: { selectable: false, deletable: false, draggable: false },
};
